import { useState } from "react"
import { CheckCircle, ShieldCheck } from "lucide-react"
import { AuthLayout, Button, SwitchMarketsBrand } from "@/components"
import { WEBSITE_URL } from "../config/index.js"
import { api } from "../system/api.js"
import { useApp } from "../hooks/useApp.jsx"

const SPONSOR = "SwitchMarkets"

/**
 * Free -> Switch Markets sponsored, for an account that registered on the
 * ordinary form and is entitled to the sponsored plan.
 *
 * Reachable by URL only -- support hands out the link. There is deliberately no
 * entry point in Settings, so nothing surfaces a plan that is invisible to the
 * plan switcher (sponsor-sm is is_visible = 0) to users who cannot use it.
 *
 * The gate below is a courtesy, not the enforcement point: the server re-checks
 * the plan and runs the Switch Markets balance check on submit, against the
 * signed-in account's own email.
 */
export default function SwitchMarketsPlanChange() {
  const { session } = useApp() ?? {}

  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState(null)
  const [success, setSuccess] = useState(false)

  const subscription = session?.subscription
  const isSponsored = subscription?.sponsor === SPONSOR
  const isFree = subscription?.slug === "free"

  // A plan change is not broadcast over SSE and AppProvider reads the session
  // once on mount, so a client-side navigation would carry the stale Free plan
  // into the app. Same reason Settings reloads after every plan change.
  const goToDashboard = () => {
    window.location.href = "/"
  }

  const handleSubmit = async () => {
    if (loading) return

    setLoading(true)
    setFormError(null)
    try {
      await api("subscription/sponsor/convert", { sponsor: SPONSOR })
      setLoading(false)
      setSuccess(true)
    } catch (err) {
      setLoading(false)
      const code = err?.errorCode
      if (code === "SPONSOR_ELIGIBILITY_ERROR") {
        setFormError(err.userMessage || "Eligibility check failed.")
      } else if (code === "CONVERSION_NOT_AVAILABLE") {
        setFormError("This option is only available on the Free plan.")
      } else if (code === "ALREADY_SPONSORED") {
        setFormError("You're already on the Switch Markets sponsored plan.")
      } else if (code === "USER_HAS_NO_ACTIVE_SUBSCRIPTION") {
        setFormError("You don't have an active plan yet.")
      } else {
        setFormError("An error occurred. Please try again.")
      }
    }
  }

  const brandSlot = <SwitchMarketsBrand />

  const footerSlot = (
    <>&copy; {new Date().getFullYear()} AlgoBuilder. All rights reserved.</>
  )

  if (success) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <CheckCircle
            size={36}
            strokeWidth={1.2}
            className="text-primary mx-auto mb-3"
          />
          <h1 className="text-page-title text-foreground mb-1.5">
            You&apos;re all set!
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed mb-4">
            Your free access as a Switch&nbsp;Markets trader has been activated.
          </p>
          <Button className="w-full text-label" onClick={goToDashboard}>
            Go to Dashboard
          </Button>
        </div>
      </AuthLayout>
    )
  }

  if (isSponsored) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <h1 className="text-page-title text-foreground mb-1.5">
            Already activated
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed mb-4">
            You&apos;re already on the Switch&nbsp;Markets sponsored plan. There&apos;s nothing to change.
          </p>
          <Button className="w-full text-label" onClick={goToDashboard}>
            Go to Dashboard
          </Button>
        </div>
      </AuthLayout>
    )
  }

  if (!isFree) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <h1 className="text-page-title text-foreground mb-1.5">
            Not available on your plan
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed mb-4">
            Switching to the Switch&nbsp;Markets sponsored plan is only available
            from the Free plan
            {subscription?.plan
              ? `, and you're currently on ${subscription.plan}.`
              : "."}{" "}
            <a
              href="https://algobuilder.com/contact-us"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Contact support
            </a>{" "}
            if you think this is wrong.
          </p>
          <Button
            variant="outline"
            className="w-full text-label"
            onClick={goToDashboard}
          >
            Go to Dashboard
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-page-title text-foreground mb-1.5">
            Switch to Switch&nbsp;Markets
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed">
            AlgoBuilder is free for all funded traders at Switch&nbsp;Markets. Confirm below to move your account from the Free plan to the sponsored plan.
          </p>
        </div>

        <div className="flex gap-2.5 rounded-md border border-border bg-surface px-3 py-2.5">
          <ShieldCheck
            size={16}
            strokeWidth={1.6}
            className="text-muted-foreground shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <p className="text-caption text-muted-foreground leading-relaxed">
            We&apos;ll check your Switch&nbsp;Markets balance using your account email
            {session?.email ? (
              <>
                , <span className="text-foreground">{session.email}</span>
              </>
            ) : null}
            . It has to match the email on your Switch&nbsp;Markets account.
          </p>
        </div>

        {formError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-caption text-destructive">
            {formError}
          </div>
        )}

        <Button
          className="w-full text-label"
          disabled={loading}
          onClick={handleSubmit}
        >
          {loading ? "Checking eligibility..." : "Confirm and switch"}
        </Button>

        <button
          type="button"
          onClick={goToDashboard}
          className="text-caption text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
      </div>
    </AuthLayout>
  )
}
